import { breakpoint } from './breakpoints';

export function guidesEntryToc() {
    const toc = document.querySelector('.guides-entry-anchors .toc');
    if (!toc) return;

    const items = [];
    toc.querySelectorAll('a[href^="#block-"]').forEach((anchor) => {
        const target = document.getElementById(anchor.getAttribute('href').slice(1));
        if (target) items.push({ target, anchor });
    });
    if (items.length === 0) return;

    const desktopMQ = breakpoint('medium');
    const intersecting = new Set();
    let observer = null;
    let activeAnchor = null;

    const updateActive = () => {
        let nextActive = null;
        // Pick the bottommost intersecting item (last in document order).
        // items is in document order, so iterate from the end.
        for (let i = items.length - 1; i >= 0; i--) {
            if (intersecting.has(items[i].target)) {
                nextActive = items[i].anchor;
                break;
            }
        }
        if (nextActive === activeAnchor) return;
        if (activeAnchor) activeAnchor.classList.remove('is-in-view');
        if (nextActive) nextActive.classList.add('is-in-view');
        activeAnchor = nextActive;
    };

    const start = () => {
        if (observer) return;
        observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) intersecting.add(entry.target);
                    else intersecting.delete(entry.target);
                }
                updateActive();
            },
            { rootMargin: '0px 0px -75% 0px' }
        );
        for (const { target } of items) observer.observe(target);
    };

    const stop = () => {
        if (!observer) return;
        observer.disconnect();
        observer = null;
        intersecting.clear();
        if (activeAnchor) {
            activeAnchor.classList.remove('is-in-view');
            activeAnchor = null;
        }
    };

    if (desktopMQ.matches) start();
    desktopMQ.addEventListener('change', (e) => (e.matches ? start() : stop()));
}

/**
 * Scrolls the current entry's nav link to the top of `scroller`.
 *
 * The scrollHeight guard is load-bearing: if the scroller isn't actually
 * scrollable, scrollIntoView keeps walking up ancestors and scrolls the *page*
 * to reveal the link. Bailing early means this can only ever move the nav — and
 * it's also what keeps short, non-scrolling navs from moving at all.
 *
 * `instant` opts out of the smooth scrolling html picks up from _global.scss,
 * which would otherwise apply if the document ever did scroll.
 */
function revealActiveNavItem(scroller) {
    if (!scroller || scroller.scrollHeight <= scroller.clientHeight) return;
    scroller
        .querySelector('.is-active > span a')
        ?.scrollIntoView({ block: 'start', behavior: 'instant' });
}

export function guidesEntryActiveNav() {
    const nav = document.querySelector('.guides-entry-nav');
    if (!nav || !breakpoint('medium').matches) return;

    // The column only becomes a scroll container once header.js has set
    // --header-height, which is what resolves its max-height — and header.js
    // does that on a rAF. Waiting a frame lets revealActiveNavItem see the
    // real scrollHeight; initHeader() runs first in app.js, so its callback is
    // queued ahead of this one. If that order ever changed the guard above
    // would simply no-op, rather than scrolling the page.
    requestAnimationFrame(() => revealActiveNavItem(nav));
}

export function guidesEntryMobileNav() {
    const dialog = document.getElementById('guides-mobile-nav');
    if (!dialog) return;

    const toggle = document.querySelector('.guides-entry-nav-toggle');
    const closeBtn = dialog.querySelector('[data-close-mobile-nav]');
    const desktopMQ = breakpoint('medium');

    toggle?.addEventListener('click', () => {
        if (desktopMQ.matches) return;
        if (dialog.open) {
            dialog.close();
        } else {
            dialog.showModal();
            toggle.setAttribute('aria-expanded', 'true');
            revealActiveNavItem(dialog);
        }
    });
    closeBtn?.addEventListener('click', () => dialog.close());
    // Sync aria-expanded for every close path: close button, ESC,
    // closedby="any" backdrop dismiss, and the desktop-resize close below.
    dialog.addEventListener('close', () => {
        toggle?.setAttribute('aria-expanded', 'false');
    });
    desktopMQ.addEventListener('change', (e) => {
        if (e.matches && dialog.open) dialog.close();
    });
}
